'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'next/navigation';
import { useTranslation } from '@/hooks/useTranslation';
import {
  siteAttendanceApi,
  type DispatchOptions,
  type DispatchOptionPartner,
} from '@/services/api/site-attendance';

const LS_PREFIX = 'sharedCheckin.lastChoice.';

type ChoiceRecord = {
  companyId: string;
  partnerId?: string;
  savedAt: number;
};

function readChoice(code: string): ChoiceRecord | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(LS_PREFIX + code);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed.companyId === 'string' ? parsed : null;
  } catch {
    return null;
  }
}

function saveChoice(code: string, choice: { companyId: string; partnerId?: string }) {
  if (typeof window === 'undefined') return;
  localStorage.setItem(
    LS_PREFIX + code,
    JSON.stringify({ ...choice, savedAt: Date.now() }),
  );
}

function clearChoice(code: string) {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(LS_PREFIX + code);
}

export default function SharedDispatchPage() {
  const params = useParams();
  const search = useSearchParams();
  const { t } = useTranslation();
  const s = t.siteAttendance as Record<string, string>;

  const code = String(params.code ?? '');
  const qrToken = search.get('t') ?? '';
  const isSwitch = search.get('switch') === '1';

  const [loading, setLoading] = useState(true);
  const [dispatching, setDispatching] = useState(false);
  const [options, setOptions] = useState<DispatchOptions | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Flatten self + partners into a single list for rendering
  type Card = {
    key: string;
    companyId: string;
    partnerId?: string;
    companyLabel: string;
    subtitle?: string;
  };
  const cards: Card[] = useMemo(() => {
    if (!options) return [];
    const list: Card[] = [
      {
        key: `self:${options.self.companyId}`,
        companyId: options.self.companyId,
        companyLabel: options.self.companyLabel,
        subtitle: options.self.checkpointName,
      },
      ...options.partners.map<Card>((p) => ({
        key: `partner:${p.partnerId}`,
        companyId: p.companyId,
        partnerId: p.partnerId,
        companyLabel: p.companyLabel,
        subtitle: p.displayLabel,
      })),
    ];
    return list;
  }, [options]);

  const doDispatch = useCallback(
    async (choice: { companyId: string; partnerId?: string }) => {
      if (!qrToken) {
        setError(s?.qrTokenMissing ?? 'Please check in via the front-desk QR code');
        return;
      }
      setDispatching(true);
      try {
        const result = await siteAttendanceApi.dispatch({
          checkpointCode: code,
          qrToken,
          choice,
        });
        saveChoice(code, choice);
        if (typeof window !== 'undefined') {
          window.location.replace(result.redirectUrl);
        }
      } catch (e: any) {
        const codeErr: string | undefined = e?.code ?? e?.response?.data?.error?.code ?? e?.response?.data?.code;
        if (codeErr === 'QR_TOKEN_EXPIRED') {
          setError(s?.qrTokenExpired ?? 'QR code expired');
        } else if (codeErr === 'QR_TOKEN_INVALID' || codeErr === 'QR_TOKEN_MALFORMED' || codeErr === 'QR_TOKEN_MISSING') {
          setError(s?.qrTokenInvalid ?? 'Invalid QR code');
        } else if (codeErr === 'DISPATCH_NOT_ENABLED') {
          setError(s?.dispatchNotEnabled ?? 'Not enabled');
        } else {
          setError(s?.dispatchFailed ?? 'Failed');
        }
        setDispatching(false);
      }
    },
    [code, qrToken, t],
  );

  useEffect(() => {
    if (isSwitch) {
      clearChoice(code);
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const opts = await siteAttendanceApi.getDispatchOptions(code, qrToken);
        if (cancelled) return;
        setOptions(opts);

        // Auto-dispatch based on localStorage if not switching
        if (!isSwitch) {
          const saved = readChoice(code);
          if (saved) {
            // Verify saved choice still valid in current options
            const isValid =
              saved.companyId === opts.self.companyId ||
              opts.partners.some(
                (p) =>
                  p.companyId === saved.companyId &&
                  (!saved.partnerId || p.partnerId === saved.partnerId),
              );
            if (isValid) {
              void doDispatch({
                companyId: saved.companyId,
                partnerId: saved.partnerId,
              });
              return;
            }
          }
        }
        setLoading(false);
      } catch (e: any) {
        if (cancelled) return;
        const codeErr: string | undefined = e?.code ?? e?.response?.data?.error?.code ?? e?.response?.data?.code;
        if (codeErr === 'QR_TOKEN_EXPIRED') setError(s?.qrTokenExpired ?? 'QR code expired');
        else if (codeErr === 'QR_TOKEN_INVALID' || codeErr === 'QR_TOKEN_MALFORMED' || codeErr === 'QR_TOKEN_MISSING') setError(s?.qrTokenInvalid ?? 'Invalid QR code');
        else if (codeErr === 'DISPATCH_NOT_ENABLED') setError(s?.dispatchNotEnabled ?? 'Not enabled');
        else setError(s?.dispatchFailed ?? 'Failed');
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code, qrToken, isSwitch, t, doDispatch]);

  // --- Render ---

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm p-8 max-w-md w-full text-center">
          <div className="text-red-500 text-5xl mb-4">⚠</div>
          <div className="text-lg font-semibold mb-4">{error}</div>
          <div className="flex gap-3 justify-center">
            <button
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700"
              onClick={() => window.location.reload()}
            >
              {s?.errorRetry ?? 'Retry'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading || dispatching) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">{s?.dispatchLoading ?? 'Loading...'}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-md mx-auto pt-8">
        <h1 className="text-2xl font-semibold text-gray-800 mb-2">
          {s?.dispatchTitle ?? 'Select your company'}
        </h1>
        <p className="text-sm text-gray-500 mb-6">{s?.dispatchHint ?? 'Pick the company you belong to'}</p>

        <div className="space-y-3">
          {cards.map((card) => (
            <button
              key={card.key}
              className="w-full bg-white rounded-2xl shadow-sm p-5 text-left hover:shadow transition-shadow active:scale-[0.99] disabled:opacity-50"
              disabled={dispatching}
              onClick={() =>
                doDispatch({ companyId: card.companyId, partnerId: card.partnerId })
              }
            >
              <div className="text-lg font-semibold text-gray-800">
                {card.companyLabel}
              </div>
              {card.subtitle && (
                <div className="text-sm text-gray-500 mt-1">
                  {s?.signInAt ?? 'Sign in at'} · {card.subtitle}
                </div>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
